import { useCallback, useMemo } from 'react';
import { History } from '@sas/react-svg';
import { createUseTextLocalizer } from '../../../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../../../locale/formatting';
import { formatNumber } from '../borrowed/catalog-app/typescript/src/app/util/format';
import { SEARCH_DELIMITER } from '../shared';
import { getCatalogSearchGroupingCharacters } from '../useCatalogSearchParser';
import { useCatalogSuggestionsData } from '../useCatalogSuggestionsData/useCatalogSuggestionsData';
import bundle from '../../NovaComponents-CatalogSearch-gui-icu.properties';
const useTextLocalizer = createUseTextLocalizer(bundle);
export const useCatalogSuggestionsList = ({
  enableExperimental,
  currentText,
  selectionStartPosition = -1,
  selectionEndPosition = -1,
  currentSearchIndices,
  suggestionDisplayOrder,
  helpUrls
}) => {
  const localize = useTextLocalizer();
  const formatLocale = useFormatLocale();
  const suggestionData = useCatalogSuggestionsData({
    enableExperimental,
    currentSearchIndices,
    currentText,
    selectionStartPosition,
    selectionEndPosition
  });
  const {
    suggestionItems,
    locations
  } = useMemo(() => {
    const items = [];
    const itemLocations = [];
    if (suggestionData) {
      for (const sugType of suggestionDisplayOrder) {
        if (sugType === 'help' && suggestionData.help) {
          if (items.length > 0) {
            items.push({
              separator: true
            });
          }
          itemLocations.push(['help', items.length]);
          items.push({
            id: suggestionData.help?.helpId,
            text: suggestionData.help?.label,
            secondaryText: suggestionData.help?.example,
            icon: suggestionData.help?.icon,
            iconAriaLabel: suggestionData.help?.iconAriaLabel,
            iconTooltip: suggestionData.help?.iconTooltip,
            suggestionType: 'help'
          });
        } else if (sugType === 'history' && suggestionData.history.length > 0) {
          if (items.length > 0) {
            items.push({
              separator: true
            });
          }
          itemLocations.push(['history', items.length]);
          const previousListItems = suggestionData.history.map(ps => {
            return {
              id: ps.query,
              text: ps.query?.replace(/\\"/g, '"'),
              secondaryText: formatNumber({
                localize,
                formatLocale,
                value: ps.count
              }),
              icon: History,
              iconTooltip: localize('search.previous.search.tooltip.label'),
              suggestionType: 'history'
            };
          });
          items.push(...previousListItems);
        } else if (sugType === 'catalog' && suggestionData.facetOrValueSuggestions.length > 0) {
          if (items.length > 0) {
            items.push({
              separator: true
            });
          }
          itemLocations.push(['catalog', items.length]);
          const facetOrValueListItems = suggestionData.facetOrValueSuggestions.map(fv => {
            const li = {
              id: fv.value || undefined,
              text: fv.label,
              // secondaryText: fv.example,
              icon: fv.icon,
              iconTooltip: fv.iconTooltip || '',
              suggestionType: 'catalog'
            };
            return li;
          });
          items.push(...facetOrValueListItems);
        }
      }
    }
    return {
      suggestionItems: items,
      locations: itemLocations
    };
  }, [suggestionData, suggestionDisplayOrder, localize, formatLocale]);
  const onSuggestionItemSelected = useCallback(suggestionEvent => {
    if (suggestionData?.parseInfo) {
      return suggestionPicker({
        currentText: currentText || '',
        filteredPrevious: suggestionData.history,
        helpUrls,
        localize,
        parseInfo: suggestionData.parseInfo,
        suggestionIndex: suggestionEvent.index,
        locations,
        facetOrValueSuggestions: suggestionData.facetOrValueSuggestions,
        suggestionItems,
        suggestionHelp: suggestionData.help
      });
    }
    return;
  }, [currentText, localize, locations, suggestionItems, helpUrls, suggestionData]);
  const result = useMemo(() => {
    return suggestionData?.parseInfo ? {
      parseInfo: suggestionData.parseInfo,
      suggestions: suggestionItems,
      onSuggestionItemSelected
    } : null;
  }, [suggestionData.parseInfo, suggestionItems, onSuggestionItemSelected]);
  return result;
};
const suggestionPicker = opts => {
  if (!opts.parseInfo) return;
  let selectedType;
  let indexInGroup = 0;
  for (let idx = opts.locations.length - 1; idx >= 0; --idx) {
    const group = opts.locations[idx];
    if (group[1] <= opts.suggestionIndex) {
      // we've found our group! now what is it? (group[0])
      selectedType = group[0];
      indexInGroup = opts.suggestionIndex - group[1];
      break;
    }
  }
  if (selectedType === 'help') {
    if (opts.helpUrls && opts.suggestionHelp && opts.helpUrls[opts.suggestionHelp.helpId]) {
      if (opts.helpUrls?.[opts.suggestionHelp.helpId] !== undefined) {
        window.open(opts.helpUrls[opts.suggestionHelp.helpId], '_blank');
      }
      return;
    }
  } else if (selectedType === 'history') {
    if (opts.filteredPrevious[indexInGroup]) {
      return {
        historySelected: true,
        historyIndices: opts.filteredPrevious[indexInGroup].indicesList,
        newInputString: opts.filteredPrevious[indexInGroup].query,
        datePickerSelected: false
      };
    }
  } else if (selectedType === 'catalog') {
    const fvSuggestion = opts.facetOrValueSuggestions[indexInGroup];
    if (!fvSuggestion) return;
    if (fvSuggestion.label === opts.localize('search.dateFilter.CustomRange.label')) {
      // TODO: I do not like this ^^^
      // should date suggestions be handled differently? or have some other indication that it is a date suggestion?
      return {
        datePickerSelected: true,
        newInputString: undefined,
        historySelected: false
      };
    }
    let newInputString = '';
    let startSuggestionDelim = '';
    let endSuggestionDelim = '';
    let suggestionValue = fvSuggestion.value;
    if (opts.parseInfo.suggestionType === 'value') {
      endSuggestionDelim = SEARCH_DELIMITER;
      const facetInfo = opts.parseInfo.facetInfo;
      if (suggestionValue && facetInfo) {
        suggestionValue = suggestionValue.replace(/\"/g, '\\"');
        const {
          opening,
          closing,
          autoClose
        } = determineGroupingCharacters({
          value: suggestionValue,
          facetParseInfo: facetInfo
        });
        startSuggestionDelim = SEARCH_DELIMITER + opening;
        if (facetInfo.groupingTokenType === 'paren') {
          const innerGroupingChars = getCatalogSearchGroupingCharacters('quote');
          const suggestedValuePrefix = facetInfo.acceptedValue.length > 0 ? `${facetInfo.acceptedValue}${SEARCH_DELIMITER}${innerGroupingChars.opening}` : innerGroupingChars.opening;
          suggestionValue = `${suggestedValuePrefix}${suggestionValue}${innerGroupingChars.closing}`;
        }
        endSuggestionDelim = autoClose ? `${closing}${SEARCH_DELIMITER}` : SEARCH_DELIMITER;
      }
    }
    newInputString = opts.currentText.substring(0, opts.parseInfo.startIndex).trimLeft() + startSuggestionDelim + suggestionValue + endSuggestionDelim;
    return {
      newInputString,
      // if input string should be changed due to the selection
      datePickerSelected: false,
      // if date picker selected
      historyIndices: undefined,
      // if history item selected
      historySelected: false,
      // when history is selected, we need to use the associated indices on the new search
      cursorOffset: fvSuggestion.cursorOffset
    };
  }
  return {
    datePickerSelected: false,
    // if date picker selected
    historyIndices: undefined,
    // if history item selected
    historySelected: false // when history is selected, we need to use the associated indices on the new search
  };
};

const determineGroupingCharacters = opts => {
  const {
    value,
    facetParseInfo
  } = opts;
  if (facetParseInfo.valueType === 'date') {
    if (value.startsWith('[')) {
      // When the value is a date and starts with a bracket, it is already formatted for search from the date
      // range picker.
      return getCatalogSearchGroupingCharacters('none');
    }
    // A date suggestion that is not a range is one of the hardcoded values (ex. Previous Day), quotes are required.
    return getCatalogSearchGroupingCharacters('quote');
  } else if (facetParseInfo.groupingTokenType !== 'none') {
    return getCatalogSearchGroupingCharacters(facetParseInfo.groupingTokenType);
  }
  // Fallback behavior is to wrap in quotes.
  return getCatalogSearchGroupingCharacters('quote');
};
//# sourceMappingURL=useCatalogSuggestionsList.js.map